import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';

export interface PhotonSuggestion {
  /** Display label e.g. "Hauptstraße 12, 8010 Graz, Österreich". */
  label: string;
  /** Street name without house number. */
  street: string;
  /** House number (Hausnummer). */
  housenumber: string;
  /** Postal code. */
  postcode: string;
  /** City / Ort. */
  city: string;
  /** ISO country code, e.g. "AT" or "DE". */
  countryCode: string | null;
  /** Latitude (WGS84). */
  lat: number | null;
  /** Longitude (WGS84). */
  lon: number | null;
}

interface PhotonFeature {
  properties: {
    name?: string;
    street?: string;
    housenumber?: string;
    postcode?: string;
    city?: string;
    state?: string;
    country?: string;
    countrycode?: string;
    osm_value?: string;
    osm_key?: string;
  };
  geometry?: { coordinates?: [number, number] };
}

interface PhotonResponse {
  features?: PhotonFeature[];
}

/**
 * Wrapper around the komoot Photon API (https://photon.komoot.io) for
 * address autocomplete. Free, OSM-based, no API key. We restrict results
 * to Germany + Austria via `lang=de` + the layer/bbox filters.
 */
@Injectable({ providedIn: 'root' })
export class PhotonService {
  private readonly http = inject(HttpClient);
  private static readonly ENDPOINT = 'https://photon.komoot.io/api/';

  search(query: string, limit = 8): Observable<PhotonSuggestion[]> {
    const q = query.trim();
    if (q.length < 3) return of([]);
    const params = new URLSearchParams({
      q,
      lang: 'de',
      limit: String(limit),
      // Bounding box covering DE + AT (incl. some neighboring slack).
      // minLon,minLat,maxLon,maxLat
      bbox: '5.5,46.0,17.5,55.5',
    });
    return this.http
      .get<PhotonResponse>(`${PhotonService.ENDPOINT}?${params.toString()}`)
      .pipe(map((res) => (res?.features ?? []).map(toSuggestion).filter(isAddress)));
  }
}

function toSuggestion(f: PhotonFeature): PhotonSuggestion {
  const p = f.properties ?? {};
  const coords = f.geometry?.coordinates;
  const street = (p.street ?? p.name ?? '').trim();
  const housenumber = (p.housenumber ?? '').trim();
  const postcode = (p.postcode ?? '').trim();
  const city = (p.city ?? '').trim();
  const country = (p.country ?? '').trim();
  const streetLine = [street, housenumber].filter(Boolean).join(' ');
  const cityLine = [postcode, city].filter(Boolean).join(' ');
  const label = [streetLine, cityLine, country].filter(Boolean).join(', ');
  return {
    label,
    street,
    housenumber,
    postcode,
    city,
    countryCode: (p.countrycode ?? '').toUpperCase() || null,
    lat: coords ? coords[1] : null,
    lon: coords ? coords[0] : null,
  };
}

function isAddress(s: PhotonSuggestion): boolean {
  // Drop suggestions that have no street info (POIs, regions, etc.).
  return !!s.street || !!s.city;
}
